


import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getSocket } from "../ChatPage/socket";
import { useAdminRealtimeSemanticFields } from "../Presence/adminRealtimeSemantic";
import { publishAdminRealtimeSemanticState } from "../Presence/adminRealtimeSemanticState";
import Layout from "../../components/Layout";
import BackButton from "../../siteSetting/components/BackButton";
import Loader from "../../components/Loader";
import ProductImgSliderModal from "../../components/ProductImgSliderModal/ProductImgSliderModal";
import PaginationComponent from "../../components/PaginationComponent";
import AdminSearchFilterPanel from "../../components/AdminSearchFilterPanel/AdminSearchFilterPanel";
import { updatedProduct } from "../../actions/api/product";
import { generatePublicUrl } from "../../urlConfig";
import {
    getBlankPriceCatalogs,
    getCatalogProductCategories,
    getPricingProductDetails,
    listPricingProducts,
} from "../CatalogReview/catalogReviewApi";
import {
    buildPayload as buildPricingPayload,
    getInitialForm as getPricingInitialForm,
    getProductPricingCardSummary,
    validateAdminProduct3DForm,
} from "./AdminProduct3DPreview/adminProduct3DPreview.utils";

const AdminProduct3DPreview = React.lazy(() => import("./AdminProduct3DPreview/AdminProduct3DPreview"));
const BlankPriceCatalogPanel = React.lazy(() => import("./BlankPriceCatalogPanel"));
const PricingPolicyPanel = React.lazy(() => import("./PricingPolicyPanel"));

const PRODUCT_SEO_UI_NAMESPACE = 'product-seo-price-v1';
const PRODUCT_SEO_UI_CHANNEL = 'workspace';
const PRODUCT_SEO_LIVE_NAMESPACE = 'product-seo-price-live-v1';
const PRODUCT_SEO_PAGE_KEY = 'product-seo-price';
const CATALOG_REVIEW_PATH = '/admin/catalog-review';

const setObjectPathValue = (source, path, value) => {
    const keys = String(path || '').split('.').filter(Boolean);
    if (!keys.length) return source;

    const root = {
        ...(source && typeof source === 'object' && !Array.isArray(source) ? source : {}),
    };
    let target = root;

    keys.forEach((key, index) => {
        if (index === keys.length - 1) {
            target[key] = value;
            return;
        }

        const current = target[key];
        target[key] = {
            ...(current && typeof current === 'object' && !Array.isArray(current)
                ? current
                : {}),
        };
        target = target[key];
    });

    return root;
};

const productFormRealtimeKey = (productId, path) => (
    `form.${String(productId || '')}.${String(path || '')}`
);

const parseProductFormRealtimeKey = (stateKey) => {
    const match = String(stateKey || '').match(/^form\.([^.]+)\.(.+)$/);
    if (!match) return null;
    return {
        productId: match[1],
        path: match[2],
    };
};


const readInitialPageMode = () => {
    if (typeof window === 'undefined') return 'products';

    const queryMode = new URLSearchParams(window.location.search).get('mode');
    if (['products', 'blank-prices', 'pricing-policy'].includes(queryMode)) return queryMode;

    const stored = window.sessionStorage.getItem('productSeoPricePage:mode');
    return ['products', 'blank-prices', 'pricing-policy'].includes(stored) ? stored : 'products';
};

const calcModeOptions = [
    { value: "fixed", label: "Фиксированная цена", field: "workPrice", suffix: "₽" },
    { value: "linearMeter", label: "Цена за метр", field: "workPricePerMeter", suffix: "₽/м" },
    { value: "squareMeter", label: "Цена за м²", field: "workPricePerSquareMeter", suffix: "₽/м²" },
    { value: "cubicMeter", label: "Цена за м³", field: "workPricePerCubicMeter", suffix: "₽/м³" },
];


const workTypeOptions = [
    { value: "milling", label: "Фрезеровка" },
    { value: "polishing", label: "Полировка" },
    { value: "engraving", label: "Гравировка" },
    { value: "installation", label: "Монтаж" },
    { value: "custom", label: "Другая работа" },
];

const tagPresets = [
    {
        label: "Крест",
        values: [
            "крест",
            "крестик",
            "с крестом",
            "с крестиком",
            "памятник с крестом",
            "памятник с крестиком"
        ]
    },
    {
        label: "Роза",
        values: [
            "роза",
            "розы",
            "с розой",
            "с розами",
            "розочка",
            "памятник с розой",
            "памятник с розами"
        ]
    },
    {
        label: "Свеча",
        values: [
            "свеча",
            "свечка",
            "со свечой",
            "свечи",
            "памятник со свечой",
            "памятник со свечами"
        ]
    },
    {
        label: "Ваза",
        values: [
            "ваза",
            "вазочка",
            "с вазой",
            "с вазами",
            "памятник с вазой",
            "памятник с вазами"
        ]
    },
    {
        label: "Ангел",
        values: [
            "ангел",
            "ангелочек",
            "с ангелом",
            "памятник с ангелом",
            "ангел на памятнике"
        ]
    },
    {
        label: "Сердце",
        values: [
            "сердце",
            "сердечко",
            "с сердцем",
            "памятник с сердцем",
            "памятник с сердечком"
        ]
    },
    {
        label: "Дерево",
        values: [
            "дерево",
            "деревья",
            "с деревом",
            "с деревьями",
            "памятник с деревом",
            "памятник с деревьями"
        ]
    },
    {
        label: "Листья",
        values: [
            "листья",
            "лист",
            "листики",
            "с листьями",
            "памятник с листьями",
            "резные листья"
        ]
    },
    {
        label: "Фигурный",
        values: [
            "фигурный",
            "фигурная форма",
            "резной",
            "резная форма",
            "нестандартная форма",
            "памятник фигурный"
        ]
    }
];

const getProductImage = (product) => {
    if (product?.reducedImage) {
        return generatePublicUrl(product.reducedImage);
    }

    const pictures = Array.isArray(product?.productPictures) ? product.productPictures : [];
    const mainPicture = pictures.find((picture) => picture.visibleImg && picture.mainImg);
    const visiblePicture = pictures.find((picture) => picture.visibleImg);
    const firstPicture = pictures[0];
    const picture = mainPicture || visiblePicture || firstPicture;

    if (picture?.img) {
        return generatePublicUrl(picture.img);
    }

    return "";
};

const getPictures = (product) => {
    return Array.isArray(product?.productPictures)
        ? product.productPictures.filter((picture) => picture?.img)
        : [];
};

const normalizeTags = (value) => {
    if (!value) return [];

    return Array.from(
        new Set(
            String(value)
                .split(/[,\n;]/)
                .map((item) => item.trim().replace(/^#+/, "").toLowerCase())
                .filter(Boolean)
        )
    );
};

const mergeTags = (currentTagsValue, nextTags) => {
    const current = normalizeTags(currentTagsValue);
    const incoming = Array.isArray(nextTags) ? nextTags : [];

    return Array.from(
        new Set(
            [...current, ...incoming]
                .map((item) => String(item || "").trim().replace(/^#+/, "").toLowerCase())
                .filter(Boolean)
        )
    ).join(", ");
};

const toNumber = (value) => {
    const prepared = String(value ?? "").replace(",", ".").trim();
    const number = Number(prepared);

    return Number.isFinite(number) ? Math.max(0, number) : 0;
};


const ProductSeoPricePage = () => {
    const [products, setProducts] = useState([]);
    const [productPagination, setProductPagination] = useState({
        page: 0,
        limit: 20,
        total: 0,
        totalPages: 1,
    });
    const [productSummary, setProductSummary] = useState({
        total: 0,
        withWork: 0,
        withoutWork: 0,
        withPricing: 0,
        withoutPricing: 0,
        withModel: 0,
        withoutModel: 0,
    });
    const [productCategories, setProductCategories] = useState([]);
    const [productOverrides, setProductOverrides] = useState({});
    const [pageMode, setPageMode] = useState(readInitialPageMode);
    const [blankPriceCatalogs, setBlankPriceCatalogs] = useState([]);
    const [blankPricesLoading, setBlankPricesLoading] = useState(false);
    const [modelPreviewProduct, setModelPreviewProduct] = useState(null);
    const [pageLoading, setPageLoading] = useState(() => readInitialPageMode() === 'products');
    const [searchPanelOpen, setSearchPanelOpen] = useState(false);
    const [showCategories, setShowCategories] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [millingFilter, setMillingFilter] = useState("all");
    const [sortMode, setSortMode] = useState("newFirst");
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [openedProductId, setOpenedProductId] = useState(null);
    const [detailsLoaded, setDetailsLoaded] = useState({});
    const [detailsLoading, setDetailsLoading] = useState({});
    const [forms, setForms] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [status, setStatus] = useState(null);
    const [photoModal, setPhotoModal] = useState({
        show: false,
        product: null,
        index: 0,
    });

    const blankCatalogsRequestedRef = useRef(false);
    const productRequestIdRef = useRef(0);
    const categoriesRequestedRef = useRef(false);

    const pendingRemoteFormPatchesRef = useRef(new Map());
    const appliedRemoteFormSignaturesRef = useRef(new Map());

    const {
        remoteState: sharedUiState,
        publishField: publishUiField,
        publishPatch: publishUiPatch,
    } = useAdminRealtimeSemanticFields({
        namespace: PRODUCT_SEO_UI_NAMESPACE,
        channelKey: PRODUCT_SEO_UI_CHANNEL,
    });

    const publishLiveState = useCallback((stateKey, value) => (
        publishAdminRealtimeSemanticState({
            namespace: PRODUCT_SEO_LIVE_NAMESPACE,
            stateKey,
            value,
        })
    ), []);

    const clearPendingRemoteFormPath = useCallback((productId, path) => {
        const id = String(productId || '');
        const pending = pendingRemoteFormPatchesRef.current.get(id);
        if (!pending) return;
        pending.delete(String(path || ''));
        if (!pending.size) pendingRemoteFormPatchesRef.current.delete(id);
    }, []);

    const applyPageModeLocal = useCallback((mode) => {
        const normalized = ['products', 'blank-prices', 'pricing-policy'].includes(mode)
            ? mode
            : 'products';

        setPageMode(normalized);

        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('productSeoPricePage:mode', normalized);
            const url = new URL(window.location.href);
            url.searchParams.set('mode', normalized);
            window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
        }
    }, []);

    const changePageMode = useCallback((mode) => {
        const normalized = ['products', 'blank-prices', 'pricing-policy'].includes(mode)
            ? mode
            : 'products';
        applyPageModeLocal(normalized);
        publishUiField('pageMode', normalized);
    }, [applyPageModeLocal, publishUiField]);

    const changeSearchPanelOpen = useCallback((nextOpen) => {
        const normalized = nextOpen === true;
        setSearchPanelOpen(normalized);
        publishUiField('searchPanelOpen', normalized);
    }, [publishUiField]);

    const changeSearchValue = useCallback((value) => {
        const normalized = String(value ?? '');
        setSearchValue(normalized);
        setPageNumber(0);
        publishUiPatch({
            searchValue: normalized,
            pageNumber: 0,
        });
    }, [publishUiPatch]);

    const changeSelectedCategory = useCallback((value) => {
        const normalized = String(value || 'all');
        setSelectedCategory(normalized);
        setPageNumber(0);
        publishUiPatch({
            selectedCategory: normalized,
            pageNumber: 0,
        });
    }, [publishUiPatch]);

    const changeMillingFilter = useCallback((value) => {
        const normalized = ['all', 'with', 'without'].includes(String(value))
            ? String(value)
            : 'all';
        setMillingFilter(normalized);
        setPageNumber(0);
        publishUiPatch({
            millingFilter: normalized,
            pageNumber: 0,
        });
    }, [publishUiPatch]);

    const changeSortMode = useCallback((value) => {
        const normalized = String(value || 'newFirst');
        setSortMode(normalized);
        setPageNumber(0);
        publishUiPatch({
            sortMode: normalized,
            pageNumber: 0,
        });
    }, [publishUiPatch]);

    const changePageSize = useCallback((value) => {
        const normalized = [10, 20, 50, 100].includes(Number(value))
            ? Number(value)
            : 20;
        setPageSize(normalized);
        setPageNumber(0);
        publishUiPatch({
            pageSize: normalized,
            pageNumber: 0,
        });
    }, [publishUiPatch]);

    const changePageNumber = useCallback((value) => {
        const normalized = Math.max(0, Math.floor(Number(value) || 0));
        setPageNumber(normalized);
        publishUiField('pageNumber', normalized);
    }, [publishUiField]);

    const changeShowCategories = useCallback((nextValue) => {
        const normalized = nextValue === true;
        setShowCategories(normalized);
        publishUiField('showCategories', normalized);
    }, [publishUiField]);


    const loadBlankPriceCatalogs = useCallback(async () => {
        try {
            setBlankPricesLoading(true);
            const response = await getBlankPriceCatalogs();
            const materials = Array.isArray(response?.materials) ? response.materials : [];
            setBlankPriceCatalogs(materials);
            return materials;
        } catch (error) {
            blankCatalogsRequestedRef.current = false;
            setStatus({
                type: 'error',
                text: error?.response?.data?.message || 'Не удалось загрузить цены заготовок',
            });
            return [];
        } finally {
            setBlankPricesLoading(false);
        }
    }, []);

    const ensureBlankPriceCatalogs = useCallback(async () => {
        if (blankCatalogsRequestedRef.current) return blankPriceCatalogs;
        blankCatalogsRequestedRef.current = true;
        return loadBlankPriceCatalogs();
    }, [blankPriceCatalogs, loadBlankPriceCatalogs]);

    useEffect(() => {
        if (pageMode !== 'blank-prices' && pageMode !== 'pricing-policy') return;
        ensureBlankPriceCatalogs();
    }, [ensureBlankPriceCatalogs, pageMode]);

    const loadProductCategories = useCallback(async () => {
        if (categoriesRequestedRef.current) return;
        categoriesRequestedRef.current = true;

        try {
            const response = await getCatalogProductCategories();
            setProductCategories(Array.isArray(response?.categories) ? response.categories : []);
        } catch (error) {
            categoriesRequestedRef.current = false;
            setStatus({
                type: 'error',
                text: error?.response?.data?.message || 'Не удалось загрузить категории товаров',
            });
        }
    }, []);

    useEffect(() => {
        if (pageMode === 'products') loadProductCategories();
    }, [loadProductCategories, pageMode]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearch(String(searchValue || '').trim());
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [searchValue]);

    const loadProductPage = useCallback(async ({ silent = false } = {}) => {
        if (pageMode !== 'products') return null;

        const requestId = productRequestIdRef.current + 1;
        productRequestIdRef.current = requestId;
        if (!silent) setPageLoading(true);

        try {
            const response = await listPricingProducts({
                mode: 'pricing',
                page: pageNumber,
                limit: pageSize,
                search: debouncedSearch,
                categoryId: selectedCategory === 'all' ? '' : selectedCategory,
                workState: millingFilter,
                sort: sortMode,
            });

            if (productRequestIdRef.current !== requestId) return null;

            setProducts(Array.isArray(response?.products) ? response.products : []);
            setProductPagination({
                page: Number(response?.pagination?.page || 0),
                limit: Number(response?.pagination?.limit || pageSize),
                total: Number(response?.pagination?.total || 0),
                totalPages: Math.max(1, Number(response?.pagination?.totalPages || 1)),
            });
            setProductSummary((current) => ({
                ...current,
                ...(response?.summary || {}),
            }));
            setStatus((current) => current?.type === 'error' ? null : current);
            return response;
        } catch (error) {
            if (productRequestIdRef.current !== requestId) return null;
            setStatus({
                type: 'error',
                text: error?.response?.data?.message || 'Не удалось загрузить товары',
            });
            return null;
        } finally {
            if (!silent && productRequestIdRef.current === requestId) {
                setPageLoading(false);
            }
        }
    }, [
        debouncedSearch,
        millingFilter,
        pageMode,
        pageNumber,
        pageSize,
        selectedCategory,
        sortMode,
    ]);

    useEffect(() => {
        loadProductPage();
    }, [loadProductPage]);

    const reloadProducts = useCallback(async () => {
        await loadProductPage({ silent: false });
    }, [loadProductPage]);

    const handleBlankProductsRecalculated = useCallback(async () => {
        await reloadProducts();
    }, [reloadProducts]);



    useEffect(() => {
        if (!sharedUiState || typeof sharedUiState !== 'object') return;

        if (['products', 'blank-prices', 'pricing-policy'].includes(String(sharedUiState.pageMode))) {
            applyPageModeLocal(String(sharedUiState.pageMode));
        }
        if (typeof sharedUiState.searchPanelOpen === 'boolean') {
            setSearchPanelOpen(sharedUiState.searchPanelOpen);
        }
        if (typeof sharedUiState.showCategories === 'boolean') {
            setShowCategories(sharedUiState.showCategories);
        }
        if (typeof sharedUiState.searchValue === 'string') {
            setSearchValue(sharedUiState.searchValue);
        }
        if (typeof sharedUiState.selectedCategory === 'string') {
            setSelectedCategory(sharedUiState.selectedCategory || 'all');
        }
        if (['all', 'with', 'without'].includes(String(sharedUiState.millingFilter))) {
            setMillingFilter(String(sharedUiState.millingFilter));
        }
        if (typeof sharedUiState.sortMode === 'string' && sharedUiState.sortMode) {
            setSortMode(sharedUiState.sortMode);
        }
        if ([10, 20, 50, 100].includes(Number(sharedUiState.pageSize))) {
            setPageSize(Number(sharedUiState.pageSize));
        }
        if (Number.isFinite(Number(sharedUiState.pageNumber))) {
            setPageNumber(Math.max(0, Math.floor(Number(sharedUiState.pageNumber) || 0)));
        }
    }, [applyPageModeLocal, sharedUiState]);

    const publishCatalogReviewNavigation = useCallback(() => {
        publishLiveState('navigate-catalog-review', {
            target: CATALOG_REVIEW_PATH,
            navigationId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        });
    }, [publishLiveState]);

    useEffect(() => {
        const socket = getSocket();

        const handleLiveState = (event = {}) => {
            if (String(event.pageKey || '') !== PRODUCT_SEO_PAGE_KEY) return;
            if (event.type !== 'state') return;
            if (event.socketId && String(event.socketId) === String(socket.id || '')) return;

            const state = event.state || {};
            if (state.namespace !== PRODUCT_SEO_LIVE_NAMESPACE) return;
            const stateKey = String(state.stateKey || '');

            if (stateKey === 'navigate-catalog-review') {
                const target = String(state.value?.target || '');
                if (target !== CATALOG_REVIEW_PATH) return;
                if (window.location.pathname === target) return;
                window.location.assign(target);
                return;
            }

            const parsed = parseProductFormRealtimeKey(stateKey);
            if (!parsed) return;

            const signature = JSON.stringify(state.value);
            const previousSignature = appliedRemoteFormSignaturesRef.current.get(stateKey);
            if (previousSignature === signature) return;
            appliedRemoteFormSignaturesRef.current.set(stateKey, signature);

            const pending = pendingRemoteFormPatchesRef.current.get(parsed.productId) || new Map();
            pending.set(parsed.path, state.value);
            pendingRemoteFormPatchesRef.current.set(parsed.productId, pending);

            setForms((current) => {
                const existing = current[parsed.productId];
                if (!existing) return current;

                return {
                    ...current,
                    [parsed.productId]: setObjectPathValue(existing, parsed.path, state.value),
                };
            });
        };

        socket.on('adminRealtime:state', handleLiveState);
        return () => socket.off('adminRealtime:state', handleLiveState);
    }, []);

    const sourceProducts = useMemo(() => {
        if (!Array.isArray(products)) return [];

        return products.map((product) => {
            const override = productOverrides[product._id];
            return override ? { ...product, ...override } : product;
        });
    }, [products, productOverrides]);

    const categories = useMemo(() => (
        (Array.isArray(productCategories) ? productCategories : [])
            .map((category) => ({
                value: String(category?._id || ''),
                label: category?.name || category?.slug || 'Без категории',
                count: Number(category?.productCount || 0),
            }))
            .filter((category) => category.value)
            .sort((left, right) => left.label.localeCompare(right.label, 'ru'))
    ), [productCategories]);

    const paginatedProducts = sourceProducts;

    const modelNavigationProducts = useMemo(() => (
        sourceProducts.filter((product) => product?._id && product?.modelPath)
    ), [sourceProducts]);

    const totalPages = Math.max(1, Number(productPagination.totalPages || 1));
    const displayedTotal = Math.max(Number(productPagination.total || 0), sourceProducts.length);
    const productsWithMilling = Number(productSummary.withWork || 0);
    const productsWithoutMilling = Number(productSummary.withoutWork || 0);
    const isInitialLoading = pageLoading && sourceProducts.length === 0;
    const isReloading = pageLoading && sourceProducts.length > 0;


    useEffect(() => {
        if (pageNumber > totalPages - 1) {
            const nextPage = Math.max(0, totalPages - 1);
            setPageNumber(nextPage);
            publishUiField('pageNumber', nextPage);
        }
    }, [pageNumber, publishUiField, totalPages]);

    useEffect(() => {
        if (!modelPreviewProduct?._id) return;

        const actualProduct = sourceProducts.find((product) => (
            String(product._id) === String(modelPreviewProduct._id)
        ));

        if (!actualProduct) {
            setModelPreviewProduct(null);
            return;
        }

        if (actualProduct !== modelPreviewProduct) {
            setModelPreviewProduct(actualProduct);
        }
    }, [sourceProducts, modelPreviewProduct]);

    const handleProductUpdated = (
        updatedProductItem,
        catalogs = blankPriceCatalogs,
        { initializeForm = true } = {}
    ) => {
        if (!updatedProductItem?._id) return;

        setProductOverrides((prev) => ({
            ...prev,
            [updatedProductItem._id]: {
                ...(prev[updatedProductItem._id] || {}),
                ...updatedProductItem,
            },
        }));
        setDetailsLoaded((prev) => ({
            ...prev,
            [updatedProductItem._id]: true,
        }));
        if (initializeForm) {
            setForms((prev) => {
                let nextForm = getPricingInitialForm(updatedProductItem, {
                    blankPriceCatalogs: catalogs,
                });
                const pending = pendingRemoteFormPatchesRef.current.get(String(updatedProductItem._id));

                if (pending) {
                    pending.forEach((value, path) => {
                        nextForm = setObjectPathValue(nextForm, path, value);
                    });
                }

                return {
                    ...prev,
                    [updatedProductItem._id]: nextForm,
                };
            });
        }

        setModelPreviewProduct((prev) => {
            if (!prev || String(prev._id) !== String(updatedProductItem._id)) return prev;
            return { ...prev, ...updatedProductItem };
        });

        setPhotoModal((prev) => {
            if (!prev.product || String(prev.product._id) !== String(updatedProductItem._id)) {
                return prev;
            }
            return {
                ...prev,
                product: { ...prev.product, ...updatedProductItem },
            };
        });
    };

    const setProductExpanded = useCallback(async (product, shouldOpen, { broadcast = true } = {}) => {
        const productId = String(product?._id || '');
        if (!productId) return;

        if (!shouldOpen) {
            setOpenedProductId(null);
            if (broadcast) publishUiField('openedProductId', '');
            return;
        }

        setOpenedProductId(productId);
        if (broadcast) publishUiField('openedProductId', productId);

        if (detailsLoaded[productId]) {
            const catalogs = await ensureBlankPriceCatalogs();
            setForms((prev) => {
                if (prev[productId]) return prev;

                let nextForm = getPricingInitialForm(productOverrides[productId] || product, {
                    blankPriceCatalogs: catalogs,
                });
                const pending = pendingRemoteFormPatchesRef.current.get(productId);
                if (pending) {
                    pending.forEach((value, path) => {
                        nextForm = setObjectPathValue(nextForm, path, value);
                    });
                }

                return {
                    ...prev,
                    [productId]: nextForm,
                };
            });
            return;
        }

        setDetailsLoading((prev) => ({ ...prev, [productId]: true }));
        try {
            const [response, catalogs] = await Promise.all([
                getPricingProductDetails(productId),
                ensureBlankPriceCatalogs(),
            ]);
            const fullProduct = response?.product;
            if (!fullProduct?._id) throw new Error('Полные данные товара не получены');
            handleProductUpdated(fullProduct, catalogs);
        } catch (error) {
            setOpenedProductId(null);
            if (broadcast) publishUiField('openedProductId', '');
            setStatus({
                type: 'error',
                text: error?.response?.data?.message || error?.message || 'Не удалось загрузить товар',
            });
        } finally {
            setDetailsLoading((prev) => ({ ...prev, [productId]: false }));
        }
    }, [
        detailsLoaded,
        ensureBlankPriceCatalogs,
        productOverrides,
        publishUiField,
    ]);

    const handleToggleProduct = useCallback((product) => {
        const productId = String(product?._id || '');
        if (!productId) return;
        const shouldOpen = String(openedProductId || '') !== productId;
        setProductExpanded(product, shouldOpen, { broadcast: true });
    }, [openedProductId, setProductExpanded]);


    const handleChange = (productId, key, value) => {
        clearPendingRemoteFormPath(productId, key);
        setForms((prev) => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                [key]: value,
            },
        }));
        publishLiveState(productFormRealtimeKey(productId, key), value);
    };

    const handlePricingChange = (productId, pricingKey, patch) => {
        setForms((prev) => {
            const currentForm = prev[productId] || {};
            const currentPricing = currentForm[pricingKey] || {};

            return {
                ...prev,
                [productId]: {
                    ...currentForm,
                    [pricingKey]: {
                        ...currentPricing,
                        ...patch,
                    },
                },
            };
        });

        Object.entries(patch || {}).forEach(([key, value]) => {
            clearPendingRemoteFormPath(productId, `${pricingKey}.${key}`);
            publishLiveState(
                productFormRealtimeKey(productId, `${pricingKey}.${key}`),
                value
            );
        });
    };

    const handleBaseDimensionChange = (productId, pricingKey, dimensionKey, value) => {
        setForms((prev) => {
            const currentForm = prev[productId] || {};
            const currentPricing = currentForm[pricingKey] || {};
            const currentBase = currentPricing.baseDimensions || {};

            return {
                ...prev,
                [productId]: {
                    ...currentForm,
                    [pricingKey]: {
                        ...currentPricing,
                        baseDimensions: {
                            ...currentBase,
                            [dimensionKey]: value,
                            unit: "mm",
                        },
                    },
                },
            };
        });

        clearPendingRemoteFormPath(
            productId,
            `${pricingKey}.baseDimensions.${dimensionKey}`
        );
        publishLiveState(
            productFormRealtimeKey(
                productId,
                `${pricingKey}.baseDimensions.${dimensionKey}`
            ),
            value
        );
    };

    const handleAddTagPreset = (productId, values) => {
        const currentForm = forms[productId] || {};
        const currentTags = currentForm.hashtags || "";

        handleChange(productId, "hashtags", mergeTags(currentTags, values));
    };

    const handleSave = async (product) => {
        const form = forms[product._id] || getPricingInitialForm(product, { blankPriceCatalogs });
        const validationErrors = validateAdminProduct3DForm(form, product, { blankPriceCatalogs });

        if (validationErrors.length) {
            setStatus({
                type: "error",
                text: validationErrors.slice(0, 3).join(" "),
            });
            return;
        }

        const payload = buildPricingPayload(form, product, { blankPriceCatalogs });

        try {
            setSavingId(product._id);
            setStatus(null);

            const res = await updatedProduct(product._id, payload);

            const updated = res?.data?.updatedProduct || {
                ...product,
                ...payload,
            };

            const normalizedUpdated = {
                ...product,
                ...updated,
                hashtags: Array.isArray(updated.hashtags)
                    ? updated.hashtags
                    : payload.hashtags,
                pricingMode: updated.pricingMode || payload.pricingMode,
                price: updated.price ?? payload.price,
                productPricing: updated.productPricing || payload.productPricing,
                materialSelectionMode: updated.materialSelectionMode || payload.materialSelectionMode,
                workType: updated.workType || payload.workType,
                workLabel: updated.workLabel || payload.workLabel,
                workCalcMode: updated.workCalcMode || payload.workCalcMode,
                workPrice: updated.workPrice ?? payload.workPrice,
                workPricePerMeter: updated.workPricePerMeter ?? payload.workPricePerMeter,
                workPricePerSquareMeter: updated.workPricePerSquareMeter ?? payload.workPricePerSquareMeter,
                workPricePerCubicMeter: updated.workPricePerCubicMeter ?? payload.workPricePerCubicMeter,
                millingPricing: updated.millingPricing || payload.millingPricing,
                workOptions: Array.isArray(updated.workOptions) ? updated.workOptions : payload.workOptions,
            };

            pendingRemoteFormPatchesRef.current.delete(String(product._id));
            handleProductUpdated(normalizedUpdated);

            setStatus({
                type: "success",
                text: res?.data?.message || "Настройки товара сохранены",
            });

        } catch (error) {
            setStatus({
                type: "error",
                text: error?.response?.data?.message || "Ошибка сохранения товара",
            });
        } finally {
            setSavingId(null);
        }
    };

    const openPhoto = async (product, index = 0, { broadcast = true } = {}) => {
        const productId = String(product?._id || '');
        if (!productId) return;

        let actualProduct = productOverrides[productId] || product;

        if (!detailsLoaded[productId]) {
            setDetailsLoading((prev) => ({ ...prev, [productId]: true }));
            try {
                const response = await getPricingProductDetails(productId);
                actualProduct = response?.product || actualProduct;
                handleProductUpdated(actualProduct, blankPriceCatalogs, {
                    initializeForm: false,
                });
            } catch (error) {
                setStatus({
                    type: 'error',
                    text: error?.response?.data?.message || 'Не удалось загрузить фотографии товара',
                });
                return;
            } finally {
                setDetailsLoading((prev) => ({ ...prev, [productId]: false }));
            }
        }

        setPhotoModal({
            show: true,
            product: actualProduct,
            index,
        });
        if (broadcast) {
            publishUiField('photoModal', {
                productId,
                index: Math.max(0, Number(index) || 0),
            });
        }
    };

    const closePhoto = ({ broadcast = true } = {}) => {
        setPhotoModal({
            show: false,
            product: null,
            index: 0,
        });
        if (broadcast) publishUiField('photoModal', null);
    };

    const handleSelectModelProduct = async (product, { broadcast = true } = {}) => {
        const productId = String(product?._id || '');
        if (!productId) return;

        const cached = productOverrides[productId];
        if (detailsLoaded[productId] && cached) {
            setModelPreviewProduct(cached);
            if (broadcast) publishUiField('modelPreviewProductId', productId);
            return;
        }

        try {
            const response = await getPricingProductDetails(productId);
            const fullProduct = response?.product || product;
            handleProductUpdated(fullProduct);
            setModelPreviewProduct(fullProduct);
            if (broadcast) publishUiField('modelPreviewProductId', productId);
        } catch (error) {
            setStatus({
                type: 'error',
                text: error?.response?.data?.message || 'Не удалось загрузить товар для 3D',
            });
        }
    };

    const changePhoto = (direction, { broadcast = true } = {}) => {
        setPhotoModal((prev) => {
            const pictures = getPictures(prev.product);

            if (!pictures.length) {
                return prev;
            }

            const nextIndex = direction === "next"
                ? (prev.index + 1) % pictures.length
                : (prev.index - 1 + pictures.length) % pictures.length;

            if (broadcast && prev.product?._id) {
                publishUiField('photoModal', {
                    productId: String(prev.product._id),
                    index: nextIndex,
                });
            }

            return {
                ...prev,
                index: nextIndex,
            };
        });
    };


    useEffect(() => {
        if (!Object.prototype.hasOwnProperty.call(sharedUiState || {}, 'openedProductId')) return;

        const targetId = String(sharedUiState.openedProductId || '');
        if (!targetId) {
            if (openedProductId !== null) setOpenedProductId(null);
            return;
        }

        if (String(openedProductId || '') === targetId && detailsLoaded[targetId]) return;

        const targetProduct = sourceProducts.find((product) => String(product?._id || '') === targetId);
        if (!targetProduct) return;

        setProductExpanded(targetProduct, true, { broadcast: false });
    }, [
        detailsLoaded,
        openedProductId,
        setProductExpanded,
        sharedUiState,
        sourceProducts,
    ]);

    useEffect(() => {
        if (!Object.prototype.hasOwnProperty.call(sharedUiState || {}, 'photoModal')) return;

        const next = sharedUiState.photoModal;
        if (!next?.productId) {
            if (photoModal.show) closePhoto({ broadcast: false });
            return;
        }

        const productId = String(next.productId);
        const index = Math.max(0, Number(next.index) || 0);
        if (
            photoModal.show
            && String(photoModal.product?._id || '') === productId
            && Number(photoModal.index || 0) === index
        ) {
            return;
        }

        const product = sourceProducts.find((item) => String(item?._id || '') === productId);
        if (product) openPhoto(product, index, { broadcast: false });
    }, [photoModal, sharedUiState, sourceProducts]);

    useEffect(() => {
        if (!Object.prototype.hasOwnProperty.call(sharedUiState || {}, 'modelPreviewProductId')) return;

        const targetId = String(sharedUiState.modelPreviewProductId || '');
        if (!targetId) {
            if (modelPreviewProduct) setModelPreviewProduct(null);
            return;
        }

        if (String(modelPreviewProduct?._id || '') === targetId) return;
        const product = sourceProducts.find((item) => String(item?._id || '') === targetId);
        if (product) handleSelectModelProduct(product, { broadcast: false });
    }, [modelPreviewProduct, sharedUiState, sourceProducts]);

    const renderLoadingPanel = (title, text) => {
        return (
            <div className="productSeoPricePage__loadingPanel">
                <Loader typeSize="90px" />
                <div>
                    <strong>{title}</strong>
                    <span>{text}</span>
                </div>
            </div>
        );
    };

    const renderPhotoModal = () => {
        const pictures = getPictures(photoModal.product);
        const picture = pictures[photoModal.index];

        return (
            <ProductImgSliderModal
                show={photoModal.show}
                handleClose={closePhoto}
                modalTitle={photoModal.product?.fullName || "Фото товара"}
                size="lg"
            >
                <div className="productSeoPricePage__photoModal">
                    {picture?.img ? (
                        <img
                            src={generatePublicUrl(picture.img)}
                            alt={photoModal.product?.fullName || "Фото товара"}
                        />
                    ) : (
                        <div className="productSeoPricePage__photoEmpty">Фото не найдено</div>
                    )}

                    {pictures.length > 1 && (
                        <div className="productSeoPricePage__photoControls">
                            <button type="button" onClick={() => changePhoto("prev")}>
                                Назад
                            </button>
                            <span>
                                {photoModal.index + 1} / {pictures.length}
                            </span>
                            <button type="button" onClick={() => changePhoto("next")}>
                                Вперёд
                            </button>
                        </div>
                    )}
                </div>
            </ProductImgSliderModal>
        );
    };

    return (
        <Layout sidebar>
            <div
                className="productSeoPricePage"
                data-admin-realtime-root="product-seo-price"
                data-admin-sync-scope="product-seo-price"
            >
                <div className="productSeoPricePage__back">
                    <BackButton text="На главную" href="/containerProductPage" />
                </div>

                <div className="productSeoPricePage__head">
                    <div>
                        <h1>Цены, работы и теги</h1>
                        <p>
                            Настройка изделий, фрезеровки, материалов заготовок и хештегов.
                        </p>
                    </div>

                    <div className="productSeoPricePage__headActions">
                        <Link
                            className="productSeoPricePage__catalogReviewLink"
                            to="/admin/catalog-review"
                            onClick={publishCatalogReviewNavigation}
                        >
                            Каталог форм · ADMIN
                        </Link>
                        <div className="productSeoPricePage__counter">
                            {isInitialLoading ? "Загрузка" : `${sourceProducts.length} / ${displayedTotal}`}
                        </div>
                    </div>
                </div>

                <div
                    className="productSeoPricePage__modeTabs"
                    data-admin-sync-scope="product-seo-mode-tabs"
                >
                    <button
                        type="button"
                        className={pageMode === 'products' ? 'is-active' : ''}
                        data-admin-sync-id="mode-products"
                        onClick={() => changePageMode('products')}
                    >
                        Товары и работы
                    </button>
                    <button
                        type="button"
                        className={pageMode === 'blank-prices' ? 'is-active' : ''}
                        data-admin-sync-id="mode-blank-prices"
                        onClick={() => changePageMode('blank-prices')}
                    >
                        Цены заготовок
                        <span>{blankPriceCatalogs.length}</span>
                    </button>
                    <button
                        type="button"
                        className={pageMode === 'pricing-policy' ? 'is-active' : ''}
                        data-admin-sync-id="mode-pricing-policy"
                        onClick={() => changePageMode('pricing-policy')}
                    >
                        Расчёт цен
                    </button>
                </div>

                {status && (
                    <div className={`productSeoPricePage__status productSeoPricePage__status--${status.type}`}>
                        {status.text}
                    </div>
                )}

                <Suspense fallback={(
                    <div className="productSeoPricePage__lazyLoading">
                        <Loader typeSize="70px" />
                        <span>Загружаем только выбранный раздел…</span>
                    </div>
                )}>
                {pageMode === 'blank-prices' ? (
                    <BlankPriceCatalogPanel
                        materials={blankPriceCatalogs}
                        loading={blankPricesLoading}
                        onReload={loadBlankPriceCatalogs}
                        onMaterialsChange={setBlankPriceCatalogs}
                        onProductsRecalculated={handleBlankProductsRecalculated}
                    />
                ) : pageMode === 'pricing-policy' ? (
                    <PricingPolicyPanel
                        materials={blankPriceCatalogs}
                        materialsLoading={blankPricesLoading}
                    />
                ) : (
                    <>
                <AdminSearchFilterPanel
                    title="Поиск и фильтры товаров"
                    summary={searchValue
                        ? `Поиск: ${searchValue}`
                        : `Работа: ${millingFilter === 'all' ? 'все' : (millingFilter === 'with' ? 'с работой' : 'без работы')} · ${pageSize} на странице`}
                    open={searchPanelOpen}
                    onOpenChange={changeSearchPanelOpen}
                    searchValue={searchValue}
                    onSearchChange={changeSearchValue}
                    searchLabel="Поиск товара"
                    searchPlaceholder="Название, артикул, категория, тег"
                    disabled={isInitialLoading}
                >
                    <div className="productSeoPricePage__toolbar">
                        <div className="productSeoPricePage__toolbarField">
                            <label>Работа</label>
                            <select
                                value={millingFilter}
                                onChange={(e) => changeMillingFilter(e.target.value)}
                                disabled={isInitialLoading}
                            >
                                <option value="all">Все товары</option>
                                <option value="without">
                                    Без работы ({productsWithoutMilling})
                                </option>
                                <option value="with">
                                    С работой ({productsWithMilling})
                                </option>
                            </select>
                        </div>

                        <div className="productSeoPricePage__toolbarField">
                            <label>Сортировка</label>
                            <select
                                value={sortMode}
                                onChange={(e) => changeSortMode(e.target.value)}
                                disabled={isInitialLoading}
                            >
                                <option value="newFirst">Новые сверху</option>
                                <option value="oldFirst">Старые сверху</option>
                                <option value="withoutMillingFirst">Сначала без работы</option>
                                <option value="withMillingFirst">Сначала с работой</option>
                                <option value="nameAsc">По названию А-Я</option>
                            </select>
                        </div>

                        <div className="productSeoPricePage__toolbarField">
                            <label>На странице</label>
                            <select
                                value={pageSize}
                                onChange={(e) => changePageSize(e.target.value)}
                                disabled={isInitialLoading}
                            >
                                <option value={10}>10 товаров</option>
                                <option value={20}>20 товаров</option>
                                <option value={50}>50 товаров</option>
                                <option value={100}>100 товаров</option>
                            </select>
                        </div>
                    </div>

                    <div className="productSeoPricePage__categoriesHead">
                        <button
                            type="button"
                            onClick={() => changeShowCategories(!showCategories)}
                            disabled={isInitialLoading}
                        >
                            {showCategories ? "Свернуть категории" : "Развернуть категории"}
                        </button>

                        <div>
                            <strong>Категории</strong>
                            <span>
                                Выбрано: {selectedCategory === "all"
                                    ? "Все категории"
                                    : categories.find((item) => item.value === selectedCategory)?.label || "Категория"}
                            </span>
                        </div>
                    </div>

                    {showCategories ? (
                        <div className="productSeoPricePage__categories">
                            <button
                                type="button"
                                className={selectedCategory === "all" ? "active" : ""}
                                onClick={() => changeSelectedCategory("all")}
                            >
                                Все категории
                                <span>{Number(productSummary.total || 0)}</span>
                            </button>

                            {categories.map((category) => (
                                <button
                                    type="button"
                                    key={category.value}
                                    className={selectedCategory === category.value ? "active" : ""}
                                    onClick={() => changeSelectedCategory(category.value)}
                                >
                                    {category.label}
                                    <span>{category.count}</span>
                                </button>
                            ))}
                        </div>
                    ) : null}
                </AdminSearchFilterPanel>

                <div className="productSeoPricePage__productsTitle">
                    <div>
                        <strong>Товары</strong>
                        <span>
                            Показано {paginatedProducts.length} из {displayedTotal}. 3D-моделей на текущем наборе: {modelNavigationProducts.length}.
                        </span>
                    </div>

                    {isReloading && (
                        <div className="productSeoPricePage__miniLoader">
                            Обновляем список...
                        </div>
                    )}
                </div>

                {isInitialLoading ? (
                    renderLoadingPanel("Загружаем товары", "Идёт получение товаров, фото, цен и тегов")
                ) : (
                    <>
                        <div className="productSeoPricePage__list">
                            {paginatedProducts.map((product) => {
                                const isOpen = String(openedProductId || '') === String(product._id || '');
                                const isDetailsLoading = detailsLoading[product._id] === true;
                                const pictures = getPictures(product);
                                const form = forms[product._id] || getPricingInitialForm(product, { blankPriceCatalogs });
                                const mode = calcModeOptions.find((item) => item.value === form.workCalcMode) || calcModeOptions[0];
                                const tags = Array.isArray(product.hashtags) ? product.hashtags : [];
                                const pricingSummary = getProductPricingCardSummary(product);
                                const productPricing = form.productPricing || {};
                                const productBaseDimensions = productPricing.baseDimensions || {};
                                const millingPricing = form.millingPricing || {};
                                const millingBaseDimensions = millingPricing.baseDimensions || {};

                                return (
                                    <div
                                        className={`productSeoPricePage__item ${!product.visibleProduct ? "is-hidden" : ""}`}
                                        key={product._id}
                                        data-admin-sync-key={`product-${product._id}`}
                                        data-admin-sync-scope={`product-${product._id}`}
                                    >
                                        <div className="productSeoPricePage__itemHead">
                                            <button
                                                type="button"
                                                className="productSeoPricePage__mainImage"
                                                onClick={() => pictures.length ? openPhoto(product, 0) : null}
                                            >
                                                {getProductImage(product) ? (
                                                    <img src={getProductImage(product)} alt={product.fullName || product.name} />
                                                ) : (
                                                    <span>no image</span>
                                                )}
                                            </button>

                                            <div className="productSeoPricePage__info">
                                                <div className="productSeoPricePage__title">
                                                    {product.fullName || product.name || "Без названия"}
                                                </div>
                                                <div className="productSeoPricePage__sub">
                                                    Артикул: {product.article || "—"} · Категория: {product.categoryName || "—"}
                                                </div>
                                                <div className="productSeoPricePage__tagsPreview">
                                                    {tags.length ? tags.slice(0, 8).map((tag) => (
                                                        <span key={tag}>#{tag}</span>
                                                    )) : (
                                                        <em>Тегов пока нет</em>
                                                    )}
                                                </div>
                                            </div>

                                            <div className={`productSeoPricePage__priceBox productSeoPricePage__priceBox--dual ${pricingSummary.complete ? "has-price" : "no-price"}`}>
                                                <span>Базовый общий размер</span>
                                                <em className="productSeoPricePage__sharedDimensions">{pricingSummary.dimensionsText}</em>

                                                <div className="productSeoPricePage__pricingSummary productSeoPricePage__pricingSummary--customer">
                                                    <div className={pricingSummary.subscriberPrice > 0 ? "is-ready" : "is-missing"}>
                                                        <small>Подписка</small>
                                                        <strong>{pricingSummary.subscriberPriceText}</strong>
                                                        <em>{pricingSummary.rulesCount} разм.</em>
                                                    </div>

                                                    <div className={pricingSummary.publicPrice > 0 ? "is-ready" : "is-missing"}>
                                                        <small>Обычная</small>
                                                        <strong>{pricingSummary.publicPriceText}</strong>
                                                        <em>{pricingSummary.worksCount ? `${pricingSummary.worksCount} работ(ы)` : 'без работ'}</em>
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                className="productSeoPricePage__openBtn"
                                                data-admin-sync-id={`product-toggle-${product._id}`}
                                                onClick={() => handleToggleProduct(product)}
                                            >
                                                {isOpen ? "Свернуть" : "Раскрыть"}
                                            </button>
                                        </div>

                                        {isOpen && isDetailsLoading ? (
                                            <div className="productSeoPricePage__expanded">
                                                {renderLoadingPanel(
                                                    "Загружаем карточку товара",
                                                    "Фото, цены, материалы и настройки подгружаются по ID"
                                                )}
                                            </div>
                                        ) : isOpen ? (
                                            <div className="productSeoPricePage__expanded">
                                                <div className="productSeoPricePage__photos">
                                                    <div className="productSeoPricePage__sectionTitle">Мини-фото</div>

                                                    {pictures.length ? (
                                                        <div className="productSeoPricePage__photoGrid">
                                                            {pictures.map((picture, index) => (
                                                                <button
                                                                    type="button"
                                                                    key={picture._id || index}
                                                                    onClick={() => openPhoto(product, index)}
                                                                >
                                                                    <img src={generatePublicUrl(picture.img)} alt={product.fullName || product.name} />
                                                                </button>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="productSeoPricePage__emptyPhotos">
                                                            У товара нет фотографий
                                                        </div>
                                                    )}

                                                    {product.modelPath && (
                                                        <button
                                                            type="button"
                                                            className="productSeoPricePage__modelBtn"
                                                            data-admin-sync-id={`product-open-3d-${product._id}`}
                                                            onClick={() => handleSelectModelProduct(product, { broadcast: true })}
                                                        >
                                                            Открыть 3D модель
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="productSeoPricePage__form">
                                                    <div className="productSeoPricePage__pricingEditor productSeoPricePage__field--wide">
                                                        <div className="productSeoPricePage__pricingEditorHead">
                                                            <strong>Базовая цена и размер</strong>
                                                            <span>Эти же значения используются в 3D-редакторе и на сайте.</span>
                                                        </div>

                                                        <div className="productSeoPricePage__pricingEditorGrid">
                                                            <section>
                                                                <label className="productSeoPricePage__checkLine">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={productPricing.enabled === true}
                                                                        onChange={(e) => handlePricingChange(product._id, "productPricing", { enabled: e.target.checked })}
                                                                    />
                                                                    <span>Изделие по размеру</span>
                                                                </label>

                                                                <div className="productSeoPricePage__baseFields">
                                                                    <label>
                                                                        <span>Высота, мм</span>
                                                                        <input type="number" min="0" value={productBaseDimensions.height ?? ""} onChange={(e) => handleBaseDimensionChange(product._id, "productPricing", "height", e.target.value)} />
                                                                    </label>
                                                                    <label>
                                                                        <span>Ширина, мм</span>
                                                                        <input type="number" min="0" value={productBaseDimensions.width ?? ""} onChange={(e) => handleBaseDimensionChange(product._id, "productPricing", "width", e.target.value)} />
                                                                    </label>
                                                                    <label>
                                                                        <span>Толщина, мм</span>
                                                                        <input type="number" min="0" value={productBaseDimensions.thickness ?? ""} onChange={(e) => handleBaseDimensionChange(product._id, "productPricing", "thickness", e.target.value)} />
                                                                    </label>
                                                                    <label>
                                                                        <span>Цена базы, ₽</span>
                                                                        <input type="number" min="0" value={productPricing.basePrice ?? ""} onChange={(e) => handlePricingChange(product._id, "productPricing", { basePrice: e.target.value })} />
                                                                    </label>
                                                                </div>
                                                            </section>

                                                            <section>
                                                                <label className="productSeoPricePage__checkLine">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={millingPricing.enabled === true}
                                                                        onChange={(e) => handlePricingChange(product._id, "millingPricing", { enabled: e.target.checked })}
                                                                        disabled={!form.workEnabled}
                                                                    />
                                                                    <span>Фрезеровка по размеру</span>
                                                                </label>

                                                                <div className="productSeoPricePage__baseFields">
                                                                    <label>
                                                                        <span>Высота, мм</span>
                                                                        <input type="number" min="0" value={millingBaseDimensions.height ?? ""} onChange={(e) => handleBaseDimensionChange(product._id, "millingPricing", "height", e.target.value)} disabled={!form.workEnabled} />
                                                                    </label>
                                                                    <label>
                                                                        <span>Ширина, мм</span>
                                                                        <input type="number" min="0" value={millingBaseDimensions.width ?? ""} onChange={(e) => handleBaseDimensionChange(product._id, "millingPricing", "width", e.target.value)} disabled={!form.workEnabled} />
                                                                    </label>
                                                                    <label>
                                                                        <span>Толщина, мм</span>
                                                                        <input type="number" min="0" value={millingBaseDimensions.thickness ?? ""} onChange={(e) => handleBaseDimensionChange(product._id, "millingPricing", "thickness", e.target.value)} disabled={!form.workEnabled} />
                                                                    </label>
                                                                    <label>
                                                                        <span>Цена клиенту базового размера, ₽</span>
                                                                        <input type="number" min="0" value={millingPricing.basePrice ?? ""} onChange={(e) => handlePricingChange(product._id, "millingPricing", { basePrice: e.target.value })} disabled={!form.workEnabled} />
                                                                    </label>
                                                                </div>
                                                            </section>
                                                        </div>
                                                    </div>

                                                    <div className="productSeoPricePage__field productSeoPricePage__field--wide">
                                                        <label>Тип выбора материала на FRONT</label>
                                                        <select
                                                            value={form.materialSelectionMode || "standard"}
                                                            onChange={(e) => handleChange(
                                                                product._id,
                                                                "materialSelectionMode",
                                                                e.target.value
                                                            )}
                                                        >
                                                            <option value="granite">Гранит — один выбор породы</option>
                                                            <option value="standard">Стандарт — цвет/текстура материалов</option>
                                                        </select>
                                                        <div className="productSeoPricePage__hint">
                                                            Для памятников выбирай «Гранит»: клиент увидит один переключатель породы, а неполированная поверхность останется фиксированной. Для оград и других материалов выбирай «Стандарт».
                                                        </div>
                                                    </div>

                                                    <div className="productSeoPricePage__field productSeoPricePage__field--wide">
                                                        <label className="productSeoPricePage__checkLine">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!form.workEnabled}
                                                                onChange={(e) => handleChange(product._id, "workEnabled", e.target.checked)}
                                                            />
                                                            <span>Показывать работу на сайте</span>
                                                        </label>
                                                        <div className="productSeoPricePage__hint">
                                                            Если выключить — у товара на публичном сайте останется только “Изделие целиком”. Цена работы вводится уже конечной для клиента; налог и сайт выделяются из неё, а не добавляются сверху.
                                                        </div>
                                                    </div>

                                                    <div className="productSeoPricePage__field">
                                                        <label>Название работы</label>
                                                        <input
                                                            type="text"
                                                            value={form.workName}
                                                            placeholder="Например: Фрезеровка"
                                                            onChange={(e) => handleChange(product._id, "workName", e.target.value)}
                                                            disabled={!form.workEnabled}
                                                        />
                                                    </div>

                                                    <div className="productSeoPricePage__field">
                                                        <label>Тип работы</label>
                                                        <select
                                                            value={form.workType}
                                                            onChange={(e) => handleChange(product._id, "workType", e.target.value)}
                                                            disabled={!form.workEnabled}
                                                        >
                                                            {workTypeOptions.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div className="productSeoPricePage__field">
                                                        <label>Тип расчёта работы</label>
                                                        <select
                                                            value={form.workCalcMode}
                                                            onChange={(e) => handleChange(product._id, "workCalcMode", e.target.value)}
                                                            disabled={!form.workEnabled}
                                                        >
                                                            {calcModeOptions.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div className="productSeoPricePage__field">
                                                        <label>{mode.label}</label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={form[mode.field]}
                                                            placeholder={`Введите цену ${mode.suffix}`}
                                                            onChange={(e) => handleChange(product._id, mode.field, e.target.value)}
                                                            disabled={!form.workEnabled}
                                                        />
                                                    </div>

                                                    <div className="productSeoPricePage__field productSeoPricePage__field--wide">
                                                        <label>Быстрые теги</label>
                                                        <div className="productSeoPricePage__presetTags">
                                                            {tagPresets.map((preset) => (
                                                                <button
                                                                    type="button"
                                                                    key={preset.label}
                                                                    onClick={() => handleAddTagPreset(product._id, preset.values)}
                                                                >
                                                                    {preset.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="productSeoPricePage__field productSeoPricePage__field--wide">
                                                        <label>Хештеги</label>
                                                        <textarea
                                                            value={form.hashtags}
                                                            placeholder="роза, крест, свеча, памятник с вазой"
                                                            onChange={(e) => handleChange(product._id, "hashtags", e.target.value)}
                                                        />
                                                        <div className="productSeoPricePage__hint">
                                                            Разделяй теги запятой, точкой с запятой или новой строкой. Символ # можно не писать.
                                                        </div>
                                                    </div>

                                                    <div className="productSeoPricePage__saveRow">
                                                        <button
                                                            type="button"
                                                            disabled={savingId === product._id}
                                                            data-admin-sync-id={`product-save-${product._id}`}
                                                            data-admin-realtime-action="server"
                                                            onClick={() => handleSave(product)}
                                                        >
                                                            {savingId === product._id ? "Сохранение..." : "Сохранить"}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}

                            {!paginatedProducts.length && (
                                <div className="productSeoPricePage__empty">
                                    Товары не найдены
                                </div>
                            )}
                        </div>

                        <PaginationComponent
                            totalAll={displayedTotal}
                            pageSize={pageSize}
                            pageNumber={pageNumber}
                            setPageNumber={changePageNumber}
                            totalPages={totalPages}
                        />
                    </>
                )}

                    </>
                )}
                </Suspense>

                {renderPhotoModal()}

                {modelPreviewProduct ? (
                    <Suspense fallback={null}>
                        <AdminProduct3DPreview
                            isOpen
                            product={modelPreviewProduct}
                            products={modelNavigationProducts}
                            categories={categories}
                            onSelectProduct={handleSelectModelProduct}
                            onProductUpdated={handleProductUpdated}
                            blankPriceCatalogs={blankPriceCatalogs}
                            onClose={() => {
                                setModelPreviewProduct(null);
                                publishUiField('modelPreviewProductId', '');
                            }}
                        />
                    </Suspense>
                ) : null}
            </div>
        </Layout>
    );
};

export default ProductSeoPricePage;
